/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2024. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2024. Todos los derechos reservados.
 */

import {Injectable} from '@angular/core';
import {MkadBaseService, URLS_SERVICE} from '@shared/services';
import {catchError, map, Observable, of} from 'rxjs';
import {
  CustomerFormInterface,
  CustomerLevelEnum,
  CustomerLevelsListInterface,
  CustomerParentsListInterface,
  CustomerParentsListPayload,
  CustomerParentUpdatePayload,
  CustomersListInterface,
  CustomersListPayload,
  CustomerSummaryInterface, CustomerSummaryParamsInterface,
  MkadSummaryContactListInterface,
} from '../../../interface';
import {HttpContext, HttpResponse} from '@angular/common/http';
import {HTTP_CODE_SUCCESS} from '@shared/constants';
import {AUTH_SHOULD_INTERCEPT} from '@shared/utils';

/**
 * Service handling calls and statuses for the customer
 * @extends MkadBaseService
 */
@Injectable({
  providedIn: 'root',
})
export class MkadCustomersService extends MkadBaseService {
  readonly customerEndpoints = URLS_SERVICE.meerkad.customer;

  /**
   * @description
   * Constructor class
   */
  constructor() {
    super();
  }

  /**
   * @description
   * Method that queries the back-end for the list of customers
   * @param params
   */
  list(params?: CustomersListPayload): Observable<CustomersListInterface[]> {
    const context = new HttpContext().set(AUTH_SHOULD_INTERCEPT, false);
    return this.http.post<CustomersListInterface[]>(this.customerEndpoints.base + this.customerEndpoints.list, params, undefined, context).pipe(
      catchError((errorResponse) => {
        this.showError(errorResponse.error.errors);
        return of([]);
      }),
      map((prevResponse: CustomersListInterface[] | undefined) => {
          if (!prevResponse) return <CustomersListInterface[]>[];
          return prevResponse;
        },
      ));
  }

  /**
   * @description
   * Method that queries the back-end for the list of customers
   */
  summary(params: CustomerSummaryParamsInterface): Observable<CustomerSummaryInterface[]> {
    return this.http.post<CustomerSummaryInterface[]>(this.customerEndpoints.base + this.customerEndpoints.summary, params).pipe(
      catchError((errorResponse) => {
        this.showError(errorResponse.error.errors);
        return of([]);
      }),
      map((prevResponse: CustomerSummaryInterface[] | undefined) => {
          if (!prevResponse) return <CustomerSummaryInterface[]>[];
          return prevResponse;
        },
      ));
  }

  /**
   * @description
   * Method that delete a specific customer
   * @param param id of the customer
   */
  delete(param: number): Observable<boolean> {
    return this.http.post<HttpResponse<boolean>>(this.customerEndpoints.base + this.customerEndpoints.delete, {id: param}).pipe(
      map((prevResponse) => {
          if (!prevResponse) return true;
          return prevResponse.status === HTTP_CODE_SUCCESS;
        },
      ));
  }

  /**
   * @description
   * Method that create a specific customer
   * @param param customer data to create
   */
  create(param: CustomerFormInterface): Observable<CustomerFormInterface> {
    return this.http.post<CustomerFormInterface>(this.customerEndpoints.base + this.customerEndpoints.create, param);
  }

  /**
   * @description
   * Method for update a specific customer
   * @param param customer data to edit
   */
  update(param: CustomerFormInterface): Observable<CustomerFormInterface> {
    return this.http.post<CustomerFormInterface>(this.customerEndpoints.base + this.customerEndpoints.update, param);
  }

  /**
   * @description
   * Method for delete a specific customer
   * @param id id of the customer
   */
  get(id: number): Observable<CustomerFormInterface> {
    return this.http.post<CustomerFormInterface>(this.customerEndpoints.base + this.customerEndpoints.get, {id: id});
  }

  summaryContact(): Observable<MkadSummaryContactListInterface[]> {
    return this.http.post<MkadSummaryContactListInterface[]>(this.customerEndpoints.base + this.customerEndpoints.summaryContact);
  }

  summaryParents(params: CustomerParentsListPayload): Observable<CustomerParentsListInterface[]> {
    return this.http.post<CustomerParentsListInterface[]>(this.customerEndpoints.base + this.customerEndpoints.parentSummary, params);
  }

  updateParent(params: CustomerParentUpdatePayload) {
    return this.http.post<CustomerParentsListInterface[]>(this.customerEndpoints.base + this.customerEndpoints.parentUpdate, params);
  }

  levelsList(): Observable<CustomerLevelsListInterface> {
    return this.http.post<CustomerLevelsListInterface>(this.baseCore + this.customerEndpoints.levelsList);
  }

}
